import { AnimatePresence } from 'framer-motion'
import { Cross as Hamburger } from 'hamburger-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { use100vh } from 'react-div-100vh'
import { useTheme } from 'styled-components'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { useWishlist } from '@components/WishlistProvider/WishlistProvider'
import {
  currenciesList,
  dataTestIds,
  promotionalBannerVariants,
} from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { useCountry } from '@velasca/hooks/use-country'
import { useHeader } from '@velasca/hooks/use-header'
import { useScrollLock } from '@velasca/hooks/use-scroll-lock'
import { useTop } from '@velasca/hooks/use-top'
import { AccountIcon } from '@velasca/icons/account'
import { CartIcon } from '@velasca/icons/cart'
import { ChatIcon } from '@velasca/icons/chat'
import { PhoneIcon } from '@velasca/icons/phone'
import { SearchIcon } from '@velasca/icons/search'
import { WhatsappIcon } from '@velasca/icons/whatsapp'
import { WishlistIcon } from '@velasca/icons/wishlist'
import { WorldIcon } from '@velasca/icons/world'
import type { MenuItem, Navbar as NavbarType } from '@velasca/types'
import type {
  LiquidLink,
  MiscHeaderData,
  PublisherMenuImage,
} from '@velasca/types/src/Navbar'
import { IconButton } from '@velasca/ui/icon-button'
import { formatCurrencies } from '@velasca/utils/format-currencies'
import { $settings } from '@velasca/utils/globals'
import { Minisearch } from '../Minisearch/Minisearch'
import PromotionalBanner from '../PromotionalBanner'
import { MenuEntriesWrapper } from './MenuEntriesWrapper/MenuEntriesWrapper'
import { MenuPage } from './MenuPage/MenuPage'
import {
  ArrowTop,
  Bar,
  CenterChunk,
  CountryLowerMenuListItem,
  EndChunk,
  Header,
  Link,
  LinkNoDecoration,
  LogoContainer,
  LowerMenu,
  LowerMenuCurrencySwitch,
  LowerMenuIconWrapper,
  LowerMenuListItem,
  LowerMenuTitle,
  MenuLists,
  Navbar,
  RightSideContent,
  ScrollLockGlobalStyles,
  StartChunk,
  StyledBrandLogoExtended,
} from './MobileNavigation.styles'
import { StoreSwitchPage } from './StoreSwitchPage/StoreSwitchPage'

interface MenuPointer {
  id: string
  level: number
  menu: MenuItem[]
  menuItem?: MenuItem
  parent?: MenuPointer
  banners?: PublisherMenuImage[]
}

const MAIN_MENU_ID = 'navbar'
const SWITCH_COUNTRY_PLACEHOLDER = '#switch-country'

const LowerMenuItem = ({
  item,
  liquidLinks,
}: {
  item: MenuItem
  liquidLinks?: LiquidLink[]
}) => {
  const liquidLink = liquidLinks?.find((link) => link.placeholder === item.url)

  const url = liquidLink ? liquidLink.value : item.url

  const icon = useMemo(() => {
    switch (item.url) {
      case '/account': {
        return <AccountIcon />
      }
      case '#liquid-whatsapp': {
        return <WhatsappIcon />
      }
      case '#phone-number': {
        return <PhoneIcon />
      }
      case '#live-chat': {
        return <ChatIcon />
      }
      default: {
        return null
      }
    }
  }, [item.url])

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (item.url === '#live-chat') {
        e.preventDefault()
        if (window && window.zE) {
          window.zE('webWidget', 'open')
        }
      }
    },
    [item.url],
  )

  if (item.url === SWITCH_COUNTRY_PLACEHOLDER) {
    return null
  }

  return (
    <LowerMenuListItem>
      <Link
        href={url}
        target={item.isTargetBlank ? '_blank' : '_self'}
        onClick={handleLinkClick}
      >
        <LowerMenuTitle>
          <LowerMenuIconWrapper>{icon}</LowerMenuIconWrapper>
          <span className="text">{item.title}</span>
        </LowerMenuTitle>
      </Link>
    </LowerMenuListItem>
  )
}

const CountrySwitchLowerMenuItem = ({
  miscHeaderData,
  openSwitchCountry,
  openSwitchCurrency,
}: {
  miscHeaderData: MiscHeaderData
  openSwitchCountry?: () => void
  openSwitchCurrency?: () => void
}) => {
  const { currency } = useExchangeRates()
  const currencies = formatCurrencies(currenciesList, $settings.language)
  const currencyData = currencies.find((c) => c.code === currency)
  const country = useCountry()

  if (!country) {
    return null
  }

  const link = miscHeaderData.liquidLinks?.find(
    (liquidLink) => liquidLink.placeholder === SWITCH_COUNTRY_PLACEHOLDER,
  )
  const url = link ? link.value : (miscHeaderData.switchCountryPage ?? '#')

  return (
    <CountryLowerMenuListItem>
      <Link
        href={url}
        onClick={(e) => {
          if (openSwitchCountry) {
            e.preventDefault()
            openSwitchCountry()
          }
        }}
      >
        <LowerMenuTitle>
          <LowerMenuIconWrapper>
            <WorldIcon />
          </LowerMenuIconWrapper>
          <span className="text">
            {country.label}{' '}
            {$settings.language ? `(${$settings.language?.toUpperCase()})` : ''}
          </span>
          <ArrowTop />
        </LowerMenuTitle>
      </Link>
      {miscHeaderData?.currency?.supported && (
        <LowerMenuCurrencySwitch onClick={openSwitchCurrency}>
          <LowerMenuTitle>
            {currencyData ? (
              <span className="text">
                {currencyData.symbol} ({currencyData.code})
              </span>
            ) : (
              <span className="text">{currency}</span>
            )}
            <ArrowTop />
          </LowerMenuTitle>
        </LowerMenuCurrencySwitch>
      )}
    </CountryLowerMenuListItem>
  )
}

export const MobileNavigation = ({
  menu,
  lowerMenu,
  promotionalBanner,
  miscHeaderData = {},
  minisearch = {},
  className = '',
  sticky = false,
}: NavbarType) => {
  const [isOpen, setIsOpen] = useState(false)
  const [switchCountryOpened, setSwitchCountryOpened] = useState(false)
  const [switchCurrencyOpened, setSwitchCurrencyOpened] = useState(false)
  const [searchActivated, setSearchActivated] = useState(false)
  const [searchOpened, setSearchOpened] = useState(false)
  const [searchTop, setSearchTop] = useState(0)
  const [searchHeightDelta, setSearchHeightDelta] = useState(0)
  const [searchAnimationComplete, setSearchAnimationComplete] = useState(true)
  const [animationDone, setAnimationDone] = useState(true)
  const promotionalBannerRef = useRef<HTMLDivElement>(null)
  const navbarRef = useRef<HTMLDivElement>(null)
  const top = useTop()
  const viewportHeight = use100vh()
  const theme = useTheme()
  const {
    isActive: isWishlistActive,
    setIsDrawerOpen: setWishlistDrawerOpen,
    itemsCount: wishlistItemsCount,
  } = useWishlist()

  const initialMenuPointer = useMemo(() => {
    return {
      id: MAIN_MENU_ID,
      menu: [...(menu ?? [])],
      level: 0,
    }
  }, [menu])

  const [menuPointer, setMenuPointer] = useState<MenuPointer>({
    ...initialMenuPointer,
  })

  const [navigationDirection, setNavigationDirection] = useState<
    'enter' | 'exit'
  >('enter')

  const setPromotionalBannerHeight = useHeader(
    (s) => s.setPromotionalBannerHeight,
  )
  const setNavbarHeight = useHeader((s) => s.setNavbarHeight)
  const navbarHeight = useHeader((s) => s.navbarHeight)

  useEffect(() => {
    const { height = 0 } =
      promotionalBannerRef?.current?.getBoundingClientRect() || {}

    setPromotionalBannerHeight(height)
  }, [setPromotionalBannerHeight])

  useEffect(() => {
    const { height = 0 } = navbarRef?.current?.getBoundingClientRect() || {}

    setNavbarHeight(height)
  }, [setNavbarHeight])

  const headerHeight = useHeader((s) => s.headerHeight)
  const promotionalBannerHeight = useHeader((s) => s.promotionalBannerHeight)

  useEffect(() => {
    if (sticky) {
      document.body.style.paddingTop = `${headerHeight}px`
    }
  }, [sticky, headerHeight])

  // Use scroll lock hook to prevent layout shift when search is active
  useScrollLock(searchActivated)

  // Search opening animation logic
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (searchActivated) {
      setSearchAnimationComplete(false)
      setIsOpen(false) // Close menu when search opens
      timeoutId = setTimeout(() => {
        setSearchTop(promotionalBannerHeight + navbarHeight)
        setSearchHeightDelta(promotionalBannerHeight + navbarHeight)
        setSearchOpened(true)
      }, 300)
    } else {
      // When search is deactivated, close it first then wait for animation to complete
      setSearchOpened(false)
      timeoutId = setTimeout(() => {
        setSearchAnimationComplete(true)
      }, 350) // Short delay to let the exit animation start
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [searchActivated, navbarHeight, promotionalBannerHeight])

  const isMainMenuShowed = useMemo(() => {
    return menuPointer.id === MAIN_MENU_ID
  }, [menuPointer.id])

  const handleSelectMenuItem = useCallback(
    (item: MenuItem) => {
      let banners: PublisherMenuImage[] | undefined

      if (menuPointer.id === MAIN_MENU_ID) {
        const menuPosition = menu?.findIndex(
          (menuItem) => menuItem.linkHandle === item.linkHandle,
        )

        if (menuPosition !== undefined && menuPosition >= 0) {
          banners = miscHeaderData.publisherMenuImages
            ?.filter((banner) => {
              return Number(banner.number) === menuPosition + 1
            })
            .slice(0, 1)
        }
      } else if (item.image) {
        banners = [
          {
            url: item.url,
            src: item.image,
            alt: item.title,
            title: item.title,
            number: 0,
          },
        ]
      }
      setMenuPointer({
        id: `menu-${item.linkHandle}`,
        menu: item.children ?? [],
        menuItem: { ...item },
        parent: { ...menuPointer },
        level: menuPointer.level + 1,
        banners,
      })
      setNavigationDirection('enter')
    },
    [menu, menuPointer, miscHeaderData.publisherMenuImages],
  )

  const handleBackMenuItem = useCallback(() => {
    if (menuPointer.parent) {
      setMenuPointer({ ...menuPointer.parent })
      setNavigationDirection('exit')
    }
  }, [menuPointer.parent])

  useEffect(() => {
    if (!isOpen && !isMainMenuShowed) {
      setMenuPointer({ ...initialMenuPointer })
    }
  }, [initialMenuPointer, isMainMenuShowed, isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSwitchCountryOpened(false)
      setSwitchCurrencyOpened(false)
    }
  }, [isOpen])

  // Close search when menu is opened
  useEffect(() => {
    if (isOpen && searchActivated) {
      setSearchActivated(false)
    }
  }, [isOpen, searchActivated])

  const { cartItemCount, setIsDrawerOpen } = useCart()

  // Derived state: search is effectively active if it's activated OR animation is not complete
  const searchEffectivelyActive = searchActivated || !searchAnimationComplete

  return (
    <>
      <ScrollLockGlobalStyles />
      <Header
        className={className}
        isOpen={isOpen}
        top={top}
        animationDone={animationDone}
        $height={viewportHeight ?? 0}
      >
        {promotionalBanner !== undefined && (
          <PromotionalBanner
            {...promotionalBanner}
            ref={promotionalBannerRef}
            variant={
              top && !isOpen && !sticky && !searchEffectivelyActive
                ? promotionalBannerVariants.SEMI_TRANSPARENT
                : promotionalBannerVariants.PRIMARY
            }
          />
        )}
        <Navbar
          isOpen={isOpen}
          top={top}
          sticky={sticky}
          searchActivated={searchEffectivelyActive}
        >
          <Bar ref={navbarRef} isOpen={isOpen} top={top}>
            <StartChunk>
              <Hamburger
                toggled={isOpen}
                size={18}
                toggle={(e) => {
                  if (!e) {
                    setAnimationDone(false)
                  }
                  if (switchCountryOpened) {
                    setSwitchCountryOpened(false)
                    setTimeout(() => {
                      setIsOpen(e)
                    }, 300)
                  } else if (switchCurrencyOpened) {
                    setSwitchCurrencyOpened(false)
                    setTimeout(() => {
                      setIsOpen(e)
                    }, 300)
                  } else if (searchActivated && e) {
                    // If search is active and we're opening menu, close search first
                    setSearchActivated(false)
                    setTimeout(() => {
                      setIsOpen(e)
                    }, 300)
                  } else {
                    setIsOpen(e)
                  }
                }}
                color={
                  isOpen || !top || sticky || searchEffectivelyActive
                    ? theme.colors.text.primary
                    : theme.colors.background
                }
              />
              {miscHeaderData.searchEnabled && (
                <IconButton
                  variant={
                    isOpen || !top || sticky || searchEffectivelyActive
                      ? 'primary'
                      : 'secondary'
                  }
                  onClick={() => {
                    setSearchActivated((status) => !status)
                  }}
                >
                  <SearchIcon />
                </IconButton>
              )}
            </StartChunk>
            <CenterChunk>
              {miscHeaderData.currentTemplate === 'index' ? (
                <LogoContainer>
                  <StyledBrandLogoExtended
                    variant={
                      isOpen || !top || sticky || searchEffectivelyActive
                        ? 'primary'
                        : 'white'
                    }
                  />
                </LogoContainer>
              ) : (
                <LinkNoDecoration href="/" title="Velasca">
                  <StyledBrandLogoExtended
                    variant={
                      isOpen || !top || sticky || searchEffectivelyActive
                        ? 'primary'
                        : 'white'
                    }
                  />
                </LinkNoDecoration>
              )}
            </CenterChunk>
            <EndChunk>
              <RightSideContent>
                {isWishlistActive && (
                  <IconButton
                    className="wishlist-btn"
                    dot={wishlistItemsCount > 0}
                    variant={
                      isOpen || !top || sticky || searchEffectivelyActive
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={() => {
                      setWishlistDrawerOpen((status) => !status)
                    }}
                  >
                    <WishlistIcon />
                  </IconButton>
                )}
                <IconButton
                  data-testid={dataTestIds.CART_ICON}
                  dot={cartItemCount > 0}
                  iconSize="1.8rem"
                  variant={
                    isOpen || !top || sticky || searchEffectivelyActive
                      ? 'primary'
                      : 'secondary'
                  }
                  onClick={() => {
                    setIsDrawerOpen(true)
                  }}
                >
                  <CartIcon data-testid={dataTestIds.CART_ICON} />
                </IconButton>
              </RightSideContent>
            </EndChunk>
          </Bar>
          <AnimatePresence>
            {isOpen && (
              <MenuEntriesWrapper>
                <MenuLists
                  key={menuPointer.id}
                  upperGap={promotionalBannerHeight}
                  transition={{ ease: 'easeOut', duration: 0.25 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{
                    x: navigationDirection === 'enter' ? '-100%' : '100%',
                    opacity: 0,
                  }}
                  initial={{
                    x: navigationDirection === 'enter' ? '100%' : '-100%',
                    opacity: 0,
                  }}
                  onAnimationComplete={() => {
                    setAnimationDone(true)
                  }}
                >
                  <MenuPage
                    menu={menuPointer.menu}
                    menuItem={menuPointer.menuItem}
                    back={menuPointer.parent ? handleBackMenuItem : undefined}
                    select={handleSelectMenuItem}
                    banners={menuPointer.banners}
                    level={menuPointer.level}
                  />
                  <LowerMenu>
                    {lowerMenu?.map((thirdLevelMenu) => {
                      return (
                        <LowerMenuItem
                          key={`third-level-${thirdLevelMenu?.title}`}
                          liquidLinks={miscHeaderData.liquidLinks}
                          item={thirdLevelMenu}
                        />
                      )
                    })}
                    <CountrySwitchLowerMenuItem
                      miscHeaderData={miscHeaderData}
                      openSwitchCountry={() => {
                        setSwitchCountryOpened(true)
                      }}
                      openSwitchCurrency={() => {
                        setSwitchCurrencyOpened(true)
                      }}
                    />
                  </LowerMenu>
                </MenuLists>
                <StoreSwitchPage
                  upperGap={navbarHeight + promotionalBannerHeight}
                  type="country"
                  opened={switchCountryOpened}
                  onClose={() => {
                    setSwitchCountryOpened(false)
                  }}
                />
                <StoreSwitchPage
                  upperGap={navbarHeight + promotionalBannerHeight}
                  type="currency"
                  currencies={miscHeaderData?.currency?.supported}
                  opened={switchCurrencyOpened}
                  onClose={() => {
                    setSwitchCurrencyOpened(false)
                  }}
                />
              </MenuEntriesWrapper>
            )}
          </AnimatePresence>
          <Minisearch
            top={searchTop}
            heightDelta={searchHeightDelta}
            visible={searchOpened}
            data={minisearch}
            onClose={() => {
              setSearchActivated(false)
            }}
          />
        </Navbar>
      </Header>
    </>
  )
}
